/**
 * Unified Chart Container - Single Pain of Glass Dashboard
 * 
 * Consolidates V2ChartContainer, EnhancedV2ChartContainer, and EnhancedV2ChartContainerV2
 * into a single, optimized component with all features.
 * 
 * Features:
 * - Time period grouping (weeks/months/quarters)
 * - Business-focused filters
 * - Year visibility controls
 * - User preferences integration
 * - Lazy loading for performance
 * - Export functionality
 * - Responsive design
 */

import React, { useState } from 'react';
import { withChartConfiguration, ChartProps, EnhancedChartProps } from './withChartConfiguration';
import {
  parseWeekLabel,
  getMonthFromISOWeek,
  getQuarterFromISOWeek,
  getMonthLabelFromISOWeek,
  getQuarterLabelFromISOWeek
} from '../utils/dateUtils';
import { V2ChartData } from '../services/V2DataService';
import { log } from '../services/LoggingService';
import { performanceMonitor } from '../services/PerformanceMonitoringService';
import { ChartErrorBoundary, withChartErrorBoundary } from './ChartErrorBoundary';
import { yearDerivationService } from '../services/YearDerivationService';
import { UnifiedChartFactory } from '../services/UnifiedChartFactory';
import { formatMonetaryValue } from '../utils/chartUtils';
import { TitleEditor } from './TitleEditor';
import { customTitleService, TitleKey } from '../services/CustomTitleService';
import { Pencil } from 'lucide-react';

// Lazy load non-critical components
const CountryFundingChartV2 = React.lazy(() => 
  import('./CountryFundingChartV2').then(module => ({ default: module.CountryFundingChartV2 }))
);
const ProductCategoryChart = React.lazy(() => 
  import('./ProductCategoryChart').then(module => ({ default: module.default }))
);
const AcquisitionCategoryChart = React.lazy(() =>
  import('./AcquisitionCategoryChart').then(module => ({ default: module.default }))
);
// const BusinessFilters = React.lazy(() =>
//   import('./BusinessFilters').then(module => ({ default: module.default }))
// );
// const YearToggleControls = React.lazy(() =>
//   import('./YearToggleControls').then(module => ({ default: module.default }))
// );

import { Download, FileSpreadsheet } from 'lucide-react';
import { YearVisibilityState } from './YearToggleControls';
import { ChartMetadata } from './ChartMetadata';
import { fundingDataToCSV, maActivityToCSV, downloadCSV, generateExportFilename } from '../utils/exportUtils';
// import { BusinessFilterOptions } from './BusinessFilters';
// Export utilities handled by individual chart components

type TimeGrouping = 'weeks' | 'months' | 'quarters';

interface AggregatedDataPoint {
  label: string;
  period: string;
  data2023: number;
  data2024: number;
  data2025: number;
  dealCounts2023?: number;
  dealCounts2024?: number;
  dealCounts2025?: number;
}

interface ChartDataPoint {
  name: string;
  value: number;
  itemStyle?: {
    color: string;
  };
}

interface UnifiedChartContainerProps {
  fundingData: V2ChartData;
  maData: V2ChartData;
  timeframe: string;
  weekOffset?: number;
  geoData?: any;
  weekLabel?: string;
  yearVisibility?: YearVisibilityState;
  onYearVisibilityChange?: (visibility: YearVisibilityState) => void;
  filters?: Record<string, string[] | undefined>;
  timeGrouping?: 'weeks' | 'months' | 'quarters';
  onTimeGroupingChange?: (grouping: 'weeks' | 'months' | 'quarters') => void;
  metadata?: {
    current_period: { start: string; end: string };
    cache_status?: string;
    response_time_ms?: number;
  };
}

/**
 * Helper function to get dynamic year mapping
 */
const getYearMapping = (chartData: V2ChartData) => {
  const yearContext = yearDerivationService.getYearContext();
  const availableYears = yearContext.availableYears;

  // Map series to years based on available years and series count
  const yearMapping: Record<number, number> = {};

  if (chartData.series.length === 2) {
    // TTM case: map to most recent years
    yearMapping[0] = availableYears[availableYears.length - 2] || 2024;
    yearMapping[1] = availableYears[availableYears.length - 1] || 2025;
  } else if (chartData.series.length === 3) {
    // 3-year view: map to available years
    yearMapping[0] = availableYears[0] || 2023;
    yearMapping[1] = availableYears[1] || 2024;
    yearMapping[2] = availableYears[2] || 2025;
  }

  return yearMapping;
};

/**
 * Aggregate data by time grouping
 * Works with V2ChartData format (x_axis and series arrays)
 */
const aggregateDataByTimeUnit = (chartData: V2ChartData, grouping: TimeGrouping): AggregatedDataPoint[] => {
  if (grouping === 'weeks') {
    // For weeks, just map the data as-is (no aggregation needed)
    const series0 = chartData.series[0];
    const series1 = chartData.series[1];
    const series2 = chartData.series[2];

    const yearMapping = getYearMapping(chartData);

    return chartData.x_axis.map((label, index) => {
      const result: AggregatedDataPoint = {
        label,
        period: label,
        data2023: 0,
        data2024: 0,
        data2025: 0,
        dealCounts2023: 0,
        dealCounts2024: 0,
        dealCounts2025: 0
      };

      // Map each series to the appropriate year using series.name
      // Handle both year-based names ("2023", "2024", "2025") and TTM names ("Trailing 12 Months", "Previous TTM")
      chartData.series.forEach((series, seriesIndex) => {
        if (series && series.data[index] !== undefined && series.name) {
          const year = parseInt(series.name, 10);

          // Check if series name is a valid year (2023, 2024, 2025)
          if (!isNaN(year)) {
            if (year === 2023) {
              result.data2023 = series.data[index];
              result.dealCounts2023 = series.deal_counts?.[index] || 0;
            } else if (year === 2024) {
              result.data2024 = series.data[index];
              result.dealCounts2024 = series.deal_counts?.[index] || 0;
            } else if (year === 2025) {
              result.data2025 = series.data[index];
              result.dealCounts2025 = series.deal_counts?.[index] || 0;
            }
          } else {
            // TTM case: series names are "Trailing 12 Months" and "Previous TTM"
            // Map by index: 0 = current (2025), 1 = previous (2024)
            if (seriesIndex === 0) {
              result.data2025 = series.data[index];
              result.dealCounts2025 = series.deal_counts?.[index] || 0;
            } else if (seriesIndex === 1) {
              result.data2024 = series.data[index];
              result.dealCounts2024 = series.deal_counts?.[index] || 0;
            }
          }
        }
      });

      return result;
    });
  }

  // For months and quarters, aggregate the weekly data
  const aggregated = new Map<string, {
    data2023: number;
    data2024: number;
    data2025: number;
    label: string;
    dealCounts2023: number;
    dealCounts2024: number;
    dealCounts2025: number;
  }>();

  chartData.x_axis.forEach((label, index) => {
    const { week, year } = parseWeekLabel(label);
    let periodKey: string;
    let periodLabel: string;

    if (grouping === 'months') {
      const isoMonth = getMonthFromISOWeek(week, year);
      const monthLabel = getMonthLabelFromISOWeek(week, year);
      periodKey = isoMonth;
      periodLabel = monthLabel;
    } else { // quarters
      const isoQuarter = getQuarterFromISOWeek(week, year);
      const quarterLabel = getQuarterLabelFromISOWeek(week, year);
      periodKey = isoQuarter;
      periodLabel = quarterLabel;
    }

    if (!aggregated.has(periodKey)) {
      aggregated.set(periodKey, {
        data2023: 0,
        data2024: 0,
        data2025: 0,
        label: periodLabel,
        dealCounts2023: 0,
        dealCounts2024: 0,
        dealCounts2025: 0
      });
    }

    const current = aggregated.get(periodKey)!;

    // Aggregate data from each series using series.name for year mapping
    // Handle both year-based names ("2023", "2024", "2025") and TTM names ("Trailing 12 Months", "Previous TTM")
    chartData.series.forEach((series, seriesIndex) => {
      if (series && series.data[index] !== undefined && series.name) {
        const year = parseInt(series.name, 10);

        // Check if series name is a valid year (2023, 2024, 2025)
        if (!isNaN(year)) {
          if (year === 2023) {
            current.data2023 += series.data[index];
            if (series.deal_counts?.[index]) {
              current.dealCounts2023 += series.deal_counts[index];
            }
          } else if (year === 2024) {
            current.data2024 += series.data[index];
            if (series.deal_counts?.[index]) {
              current.dealCounts2024 += series.deal_counts[index];
            }
          } else if (year === 2025) {
            current.data2025 += series.data[index];
            if (series.deal_counts?.[index]) {
              current.dealCounts2025 += series.deal_counts[index];
            }
          }
        } else {
          // TTM case: series names are "Trailing 12 Months" and "Previous TTM"
          // Map by index: 0 = current (2025), 1 = previous (2024)
          if (seriesIndex === 0) {
            current.data2025 += series.data[index];
            if (series.deal_counts?.[index]) {
              current.dealCounts2025 += series.deal_counts[index];
            }
          } else if (seriesIndex === 1) {
            current.data2024 += series.data[index];
            if (series.deal_counts?.[index]) {
              current.dealCounts2024 += series.deal_counts[index];
            }
          }
        }
      }
    });
  });

  return Array.from(aggregated.entries())
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([period, data]) => ({
      label: data.label,
      period,
      data2023: data.data2023,
      data2024: data.data2024,
      data2025: data.data2025,
      dealCounts2023: data.dealCounts2023,
      dealCounts2024: data.dealCounts2024,
      dealCounts2025: data.dealCounts2025
    }));
};

/**
 * Get chart configuration for V2 charts using UnifiedChartFactory
 * Restores original professional styling with proper titles, spacing, and formatting
 */
const getV2ChartOption = (props: ChartProps): any => {
  const { data, timeframe, weekOffset = 0, timeGrouping, yearVisibility } = props;

  // Type guard to ensure data is V2ChartData
  if (!data || typeof data !== 'object' || !('x_axis' in data) || !('series' in data)) {
    return {
      title: { text: 'No Data Available' },
      xAxis: { type: 'category', data: [] },
      yAxis: { type: 'value' },
      series: []
    };
  }

  if (!data || !data.x_axis || !data.series) {
    return {
      title: { text: 'No Data Available' },
      xAxis: { type: 'category', data: [] },
      yAxis: { type: 'value' },
      series: []
    };
  }

  // Determine if this is M&A or funding based on title
  const isMA = data.title.toLowerCase().includes('m&a') || data.title.toLowerCase().includes('acquisition');
  const isTTM = timeframe === 'ttm';


  // Use UnifiedChartFactory with titles from data object
  // If custom titles exist, they're already set on data.title/subtitle by aggregatedFundingData/aggregatedMAData
  // If no custom titles, data.title/subtitle contain original values
  // Empty strings would force dynamic generation and ignore custom titles
  const option = UnifiedChartFactory.createYearOverYearChart(
    data,
    data.title, // Use data.title (includes custom titles if they exist)
    data.subtitle, // Use data.subtitle (includes custom titles if they exist)
    isMA ? 'count' : 'currency',
    {
      timeframe: (timeframe || '12w') as '1w' | '2w' | '4w' | '12w' | '6m' | 'ytd' | 'ttm' | 'custom',
      isMA: isMA,
      isTTM: isTTM,
      timeGrouping: (timeGrouping || 'weeks') as 'weeks' | 'months' | 'quarters',
      yearVisibility: yearVisibility,
      weekOffset: weekOffset
    }
  );

  // Set legend selected state based on year visibility
  // Check series names to distinguish between 3-year view and TTM view
  // (after filtering, 3-year view might have fewer than 3 series)
  if (yearVisibility && option.legend && !Array.isArray(option.legend)) {
    const selected: Record<string, boolean> = {};

    // Check if this is a year-based series (3-year view) or TTM
    const isYearBased = data.series.some(s => ['2023', '2024', '2025'].includes(s.name));

    if (isYearBased) {
      // 3-year view - use series.name to ensure correct mapping
      // Series may be filtered, so iterate through what exists
      data.series.forEach(series => {
        if (series.name === '2023') {
          selected['2023'] = yearVisibility.show2023 !== false;
        } else if (series.name === '2024') {
          selected['2024'] = yearVisibility.show2024 !== false;
        } else if (series.name === '2025') {
          selected['2025'] = yearVisibility.show2025 !== false;
        }
      });
    } else {
      // TTM case - series names are "Trailing 12 Months", "Previous TTM", etc.
      const series0Name = data.series[0]?.name;
      const series1Name = data.series[1]?.name;
      if (series0Name && series1Name) {
        selected[series0Name] = yearVisibility.show2024 !== false;
        selected[series1Name] = yearVisibility.show2025 !== false;
      }
    }

    // Apply selected state to legend
    option.legend.selected = selected;
  }

  // Enhanced tooltip with deal counts
  const tooltipOption = Array.isArray(option.tooltip) ? option.tooltip[0] : option.tooltip;
  option.tooltip = {
    ...tooltipOption,
    formatter: (params: any) => {
      const headerSize = 16;
      const valueSize = 14;

      let result = `<div style="font-weight: 600; font-size: ${headerSize + 2}px; margin-bottom: 8px;">${params[0].axisValueLabel}</div>`;

      params.forEach((item: any) => {
        const value = item.value;
        const dataIndex = item.dataIndex;
        const seriesIndex = item.seriesIndex;

        // Find the corresponding series to get deal_counts
        const seriesData = data.series[seriesIndex];
        const dealCount = seriesData?.deal_counts?.[dataIndex];

        const formattedValue = isMA ?
          `${value} acquisition${value !== 1 ? 's' : ''}` :
          formatMonetaryValue(value);

        result += `<div style="margin: 4px 0; font-size: ${valueSize + 2}px;">`;
        result += `<span style="display: inline-block; width: 10px; height: 10px; border-radius: 50%; background-color: ${item.color}; margin-right: 5px;"></span>`;
        result += `<span style="font-weight: 500;">${item.seriesName}:</span> `;
        result += `<span style="font-weight: 700;">${formattedValue}</span>`;

        if (dealCount && !isMA) {
          result += ` <span style="color: #6b7280;">(${dealCount} deal${dealCount !== 1 ? 's' : ''})</span>`;
        }

        result += `</div>`;
      });

      return result;
    }
  };

  return option;
};

/**
 * V2 Chart Component - Consistent with bottom 3 charts
 * Includes download buttons following DRY principles
 */
interface V2ChartComponentProps extends ChartProps {
  chartType?: string;
  onEditClick?: () => void;
  hasCustomTitle?: boolean;
}

const V2ChartComponent: React.FC<V2ChartComponentProps & EnhancedChartProps> = (props) => {
  const { chartRef, downloadChart, isLoading, data, chartType, onEditClick, hasCustomTitle } = props;

  // Determine if this is M&A chart based on data title or chartType prop
  const isMA = data && (
    (typeof data === 'object' && 'title' in data && typeof data.title === 'string' &&
     (data.title.toLowerCase().includes('m&a') || data.title.toLowerCase().includes('acquisition'))) ||
    chartType === 'ma'
  );

  // CSV Export handler
  const handleCSVExport = () => {
    if (!data || typeof data !== 'object' || !('x_axis' in data) || !('series' in data)) return;

    const csvContent = isMA ? maActivityToCSV(data) : fundingDataToCSV(data);
    const prefix = isMA ? 'ma-activity' : 'funding-trends';
    const filename = generateExportFilename(prefix, 'csv');
    downloadCSV(csvContent, filename);
  };

  return (
    <div className="chart-container relative">
      {/* Action buttons - hidden on mobile for less crowding */}
      <div className="hidden sm:flex absolute top-4 right-4 z-10 gap-1.5">
        {onEditClick && (
          <button
            onClick={onEditClick}
            className="p-1.5 bg-white dark:bg-gray-700 hover:bg-gray-100 dark:hover:bg-gray-600 rounded shadow-sm border border-gray-300 dark:border-gray-600 transition-colors group"
            title="Edit chart title"
          >
            <Pencil className="w-3 h-3 text-gray-600 dark:text-gray-300 group-hover:text-purple-600 dark:group-hover:text-purple-400" />
            {hasCustomTitle && (
              <span className="absolute -top-0.5 -right-0.5 w-2 h-2 bg-purple-600 rounded-full border border-white dark:border-gray-800" />
            )}
          </button>
        )}
        <button
          onClick={handleCSVExport}
          className="chart-button chart-button-secondary text-xs px-2 py-1.5"
          title="Export data as CSV"
          disabled={isLoading}
        >
          <FileSpreadsheet className="w-3 h-3" />
          CSV
        </button>
        <button
          onClick={() => downloadChart('blog')}
          className="chart-button chart-button-primary text-xs px-2 py-1.5"
          title="Download chart as PNG"
          disabled={isLoading}
        >
          <Download className="w-3 h-3" />
          PNG
        </button>
      </div>

      {/* Chart canvas */}
      <div
        ref={chartRef}
        className="w-full"
        style={{ minHeight: '500px' }}
      />
    </div>
  );
};

// Create wrapped components with error boundaries
const FundingChartWithHOC = withChartErrorBoundary(
  withChartConfiguration(V2ChartComponent, getV2ChartOption),
  'Funding Chart'
);
const MAChartWithHOC = withChartErrorBoundary(
  withChartConfiguration(V2ChartComponent, getV2ChartOption),
  'M&A Chart'
);

/**
 * Main Unified Chart Container
 */
export const UnifiedChartContainer: React.FC<UnifiedChartContainerProps> = ({
  fundingData,
  maData,
  timeframe,
  weekOffset = 0,
  geoData,
  weekLabel,
  yearVisibility: propYearVisibility,
  onYearVisibilityChange,
  filters,
  timeGrouping: propTimeGrouping,
  onTimeGroupingChange,
  metadata
}) => {
  // Time grouping state - use prop if provided, otherwise local state
  const [localSelectedGrouping, setLocalSelectedGrouping] = useState<TimeGrouping>('weeks');
  const selectedGrouping = propTimeGrouping || localSelectedGrouping;
  const setSelectedGrouping = onTimeGroupingChange || setLocalSelectedGrouping;

  // Business-focused filters - REMOVED: Legacy filters are no longer functional
  // Chart visibility is now always "all" (both funding and M&A shown)

  // Year visibility controls
  const [localYearVisibility, setLocalYearVisibility] = useState<YearVisibilityState>({
    show2023: true,
    show2024: true,
    show2025: true
  });

  // Download functionality is handled by individual chart components via HOC

  // Use prop visibility if provided, otherwise use local state
  const yearVisibility = propYearVisibility || localYearVisibility;
  const setYearVisibility = propYearVisibility ? () => {} : setLocalYearVisibility;

  // Title editor state
  const [fundingEditorOpen, setFundingEditorOpen] = useState(false);
  const [maEditorOpen, setMaEditorOpen] = useState(false);
  const [forceRefresh, setForceRefresh] = useState(0);

  // Generate title keys for custom title storage
  const fundingTitleKey: TitleKey = React.useMemo(() => ({
    chartType: 'funding' as const,
    timeframe,
    filters
  }), [timeframe, filters]);

  const maTitleKey: TitleKey = React.useMemo(() => ({
    chartType: 'ma' as const,
    timeframe,
    filters
  }), [timeframe, filters]);

  // Check for custom titles - use useMemo to re-compute when forceRefresh changes
  const fundingCustomTitle = React.useMemo(() =>
    customTitleService.getCustomTitle(fundingTitleKey),
    [fundingTitleKey, forceRefresh]
  );
  const maCustomTitle = React.useMemo(() =>
    customTitleService.getCustomTitle(maTitleKey),
    [maTitleKey, forceRefresh]
  );

  // Force re-render when custom titles are loaded from localStorage on page load
  React.useEffect(() => {
    if (forceRefresh === 0 && (fundingCustomTitle || maCustomTitle)) {
      // Increment forceRefresh to trigger chart update with custom titles
      setForceRefresh(1);
    }
  }, []);

  // Handle legend selection changes (when users click legend items on the chart)
  const handleLegendSelectChanged = React.useCallback((selected: Record<string, boolean>) => {
    if (!onYearVisibilityChange) return;

    // Map legend selections back to year visibility state for subtitle updates
    const newVisibility: YearVisibilityState = {
      show2023: selected['2023'] !== false,
      show2024: selected['2024'] !== false,
      show2025: selected['2025'] !== false
    };

    onYearVisibilityChange(newVisibility);
  }, [onYearVisibilityChange]);

  const timeGroupings: { key: TimeGrouping; label: string }[] = [
    { key: 'weeks', label: 'Weeks' },
    { key: 'months', label: 'Months' },
    { key: 'quarters', label: 'Quarters' }
  ];

  const containerSpacing = 'space-y-6';

  // Determine which charts to show - always show both now that legacy filters are removed
  const showFunding = true;
  const showMA = true;

  // Aggregate and transform data based on selected grouping
  const aggregatedFundingData = React.useMemo(() => {
    if (!fundingData) return null;

    const isTTM = timeframe === 'ttm';

    // CRITICAL: Filter series BEFORE aggregation so aggregateDataByTimeUnit only processes visible years
    const filteredSeries = fundingData.series.filter((series) => {
      if (isTTM) return true; // Don't filter TTM
      if (series.name === '2023') return yearVisibility.show2023;
      if (series.name === '2024') return yearVisibility.show2024;
      if (series.name === '2025') return yearVisibility.show2025;
      return true;
    });

    // Create filtered fundingData with only visible series
    const filteredFundingData = {
      ...fundingData,
      series: filteredSeries
    };

    // NOW aggregate with filtered data - this ensures data2023/2024/2025 only contain visible years
    const aggregated = aggregateDataByTimeUnit(filteredFundingData, selectedGrouping);

    // Recalculate summary totals based on visible series only
    let recalculatedSummary = { ...fundingData.summary };
    if (!isTTM && fundingData.summary) {
      // Sort visible series by year (descending - most recent first)
      const visibleSeriesSorted = [...filteredSeries].sort((a, b) => {
        const yearA = parseInt(a.name, 10);
        const yearB = parseInt(b.name, 10);
        return yearB - yearA; // Sort descending (most recent first)
      });

      let visibleCurrentAmount = 0;
      let visibleCurrentDeals = 0;
      let visiblePreviousAmount = 0;
      let visiblePreviousDeals = 0;

      // Current = most recent visible year (NOT sum of all years)
      if (visibleSeriesSorted.length >= 1) {
        const currentSeries = visibleSeriesSorted[0];
        visibleCurrentAmount = currentSeries.data.reduce((s, val) => s + val, 0);
        visibleCurrentDeals = currentSeries.deal_counts?.reduce((s, val) => s + val, 0) || 0;
      }

      // Previous = second most recent visible year
      if (visibleSeriesSorted.length >= 2) {
        const previousSeries = visibleSeriesSorted[1];
        visiblePreviousAmount = previousSeries.data.reduce((s, val) => s + val, 0);
        visiblePreviousDeals = previousSeries.deal_counts?.reduce((s, val) => s + val, 0) || 0;
      }

      // Update summary with recalculated values
      recalculatedSummary = {
        ...fundingData.summary,
        current_amount: visibleCurrentAmount,
        current_deals: visibleCurrentDeals,
        previous_amount: visiblePreviousAmount,
        previous_deals: visiblePreviousDeals,
        growth_percent: visiblePreviousAmount > 0
          ? ((visibleCurrentAmount - visiblePreviousAmount) / visiblePreviousAmount) * 100
          : visibleCurrentAmount > 0 ? 100 : 0
      };
    }

    // Generate dynamic subtitle based on visible years, using backend format
    // Backend subtitle format: "2023: $11.2B | 2024: $11.6B (+4%) | 2025: $15.9B (+37%) — Year-to-Date"
    let adjustedSubtitle = fundingCustomTitle?.subtitle || fundingData.subtitle;

    if (!fundingCustomTitle && !isTTM && filteredSeries.length > 0) {
      // Extract timeframe description from backend subtitle (after "—")
      const timeframeMatch = fundingData.subtitle.match(/—\s*(.+)$/);
      const timeframeDesc = timeframeMatch ? timeframeMatch[1] : 'Year-to-Date';

      // Build year parts in chronological order with YoY growth
      const yearParts: string[] = [];

      // Sort filtered series by year (ascending for chronological order)
      const sortedSeries = [...filteredSeries].sort((a, b) => {
        const yearA = parseInt(a.name, 10);
        const yearB = parseInt(b.name, 10);
        return yearA - yearB;
      });

      sortedSeries.forEach((series, index) => {
        const year = series.name;
        const total = series.data.reduce((sum, val) => sum + val, 0);
        const formatted = `$${(total / 1e9).toFixed(1)}B`;

        if (index === 0) {
          // First year - no growth calculation
          yearParts.push(`${year}: ${formatted}`);
        } else {
          // Calculate growth vs previous visible year
          const prevSeries = sortedSeries[index - 1];
          const prevTotal = prevSeries.data.reduce((sum, val) => sum + val, 0);
          const growth = prevTotal > 0 ? ((total - prevTotal) / prevTotal) * 100 : 0;
          const sign = growth >= 0 ? '+' : '';
          yearParts.push(`${year}: ${formatted} (${sign}${growth.toFixed(0)}%)`);
        }
      });

      adjustedSubtitle = `${yearParts.join(' | ')} — ${timeframeDesc}`;
    }

    // For TTM with 2 series: index 0 = 2024, index 1 = 2025
    // For 3-series: index 0 = 2023, index 1 = 2024, index 2 = 2025
    const seriesCount = fundingData.series.length;

    // Build result object with custom title applied inline (not mutated)
    // This ensures React detects the change
    const result = {
      ...fundingData,
      x_axis: aggregated.map(d => d.label),
      // Apply custom title inline to avoid mutation
      title: fundingCustomTitle?.title || fundingData.title,
      subtitle: adjustedSubtitle,
      summary: recalculatedSummary, // Use recalculated summary based on visible series
      series: filteredSeries.map((series) => ({
        ...series,
        data: aggregated.map(d => {
          // Use series.name to map to the correct data, not index
          const year = parseInt(series.name, 10);
          if (!isNaN(year)) {
            // Year-based series (2023, 2024, 2025)
            if (year === 2023) return d.data2023;
            if (year === 2024) return d.data2024;
            if (year === 2025) return d.data2025;
          } else {
            // TTM series names
            if (series.name === 'Trailing 12 Months') return d.data2025;
            if (series.name === 'Previous TTM') return d.data2024;
          }
          return 0;
        }),
        deal_counts: aggregated.map(d => {
          // Use series.name to map to the correct deal counts, not index
          const year = parseInt(series.name, 10);
          if (!isNaN(year)) {
            // Year-based series (2023, 2024, 2025)
            if (year === 2023) return d.dealCounts2023 || 0;
            if (year === 2024) return d.dealCounts2024 || 0;
            if (year === 2025) return d.dealCounts2025 || 0;
          } else {
            // TTM series names
            if (series.name === 'Trailing 12 Months') return d.dealCounts2025 || 0;
            if (series.name === 'Previous TTM') return d.dealCounts2024 || 0;
          }
          return 0;
        })
      }))
    };

    return result;
  }, [fundingData, selectedGrouping, yearVisibility, timeframe, fundingCustomTitle, forceRefresh]);

  const aggregatedMAData = React.useMemo(() => {
    if (!maData) return null;

    const isTTM = timeframe === 'ttm';

    // CRITICAL: Filter series BEFORE aggregation so aggregateDataByTimeUnit only processes visible years
    const filteredSeries = maData.series.filter((series) => {
      if (isTTM) return true; // Don't filter TTM
      if (series.name === '2023') return yearVisibility.show2023;
      if (series.name === '2024') return yearVisibility.show2024;
      if (series.name === '2025') return yearVisibility.show2025;
      return true;
    });

    // Create filtered maData with only visible series
    const filteredMAData = {
      ...maData,
      series: filteredSeries
    };

    // NOW aggregate with filtered data - this ensures data2023/2024/2025 only contain visible years
    const aggregated = aggregateDataByTimeUnit(filteredMAData, selectedGrouping);

    // Recalculate summary totals based on visible series only
    let recalculatedSummary = { ...maData.summary };
    if (!isTTM && maData.summary) {
      // Sort visible series by year (descending - most recent first)
      const visibleSeriesSorted = [...filteredSeries].sort((a, b) => {
        const yearA = parseInt(a.name, 10);
        const yearB = parseInt(b.name, 10);
        return yearB - yearA; // Sort descending (most recent first)
      });

      let visibleCurrentCount = 0;
      let visiblePreviousCount = 0;

      // Current = most recent visible year (NOT sum of all years)
      if (visibleSeriesSorted.length >= 1) {
        const currentSeries = visibleSeriesSorted[0];
        visibleCurrentCount = currentSeries.data.reduce((s, val) => s + val, 0);
      }

      // Previous = second most recent visible year
      if (visibleSeriesSorted.length >= 2) {
        const previousSeries = visibleSeriesSorted[1];
        visiblePreviousCount = previousSeries.data.reduce((s, val) => s + val, 0);
      }

      // Update summary with recalculated values
      recalculatedSummary = {
        ...maData.summary,
        current_count: visibleCurrentCount,
        previous_count: visiblePreviousCount,
        growth_percent: visiblePreviousCount > 0
          ? ((visibleCurrentCount - visiblePreviousCount) / visiblePreviousCount) * 100
          : visibleCurrentCount > 0 ? 100 : 0
      };
    }

    // Generate dynamic subtitle based on visible years, using backend format
    // Backend subtitle format: "2023: 208 | 2024: 212 deals (+2%) | 2025: 257 deals (+21%) — Year-to-Date"
    let adjustedSubtitle = maCustomTitle?.subtitle || maData.subtitle;

    if (!maCustomTitle && !isTTM && filteredSeries.length > 0) {
      // Extract timeframe description from backend subtitle (after "—")
      const timeframeMatch = maData.subtitle.match(/—\s*(.+)$/);
      const timeframeDesc = timeframeMatch ? timeframeMatch[1] : 'Year-to-Date';

      // Build year parts in chronological order with YoY growth
      const yearParts: string[] = [];

      // Sort filtered series by year (ascending for chronological order)
      const sortedSeries = [...filteredSeries].sort((a, b) => {
        const yearA = parseInt(a.name, 10);
        const yearB = parseInt(b.name, 10);
        return yearA - yearB;
      });

      sortedSeries.forEach((series, index) => {
        const year = series.name;
        const total = series.data.reduce((sum, val) => sum + val, 0);

        if (index === 0) {
          // First year - no growth calculation
          yearParts.push(`${year}: ${total}`);
        } else {
          // Calculate growth vs previous visible year
          const prevSeries = sortedSeries[index - 1];
          const prevTotal = prevSeries.data.reduce((sum, val) => sum + val, 0);
          const growth = prevTotal > 0 ? ((total - prevTotal) / prevTotal) * 100 : 0;
          const sign = growth >= 0 ? '+' : '';
          yearParts.push(`${year}: ${total} deals (${sign}${growth.toFixed(0)}%)`);
        }
      });

      adjustedSubtitle = `${yearParts.join(' | ')} — ${timeframeDesc}`;
    }

    // For TTM with 2 series: index 0 = 2024, index 1 = 2025
    // For 3-series: index 0 = 2023, index 1 = 2024, index 2 = 2025
    const seriesCount = maData.series.length;

    // Build result object with custom title applied inline (not mutated)
    // This ensures React detects the change
    const result = {
      ...maData,
      x_axis: aggregated.map(d => d.label),
      // Apply custom title inline to avoid mutation
      title: maCustomTitle?.title || maData.title,
      subtitle: adjustedSubtitle,
      summary: recalculatedSummary, // Use recalculated summary based on visible series
      series: filteredSeries.map((series) => ({
        ...series,
        data: aggregated.map(d => {
          // Use series.name to map to the correct data, not index
          const year = parseInt(series.name, 10);
          if (!isNaN(year)) {
            // Year-based series (2023, 2024, 2025)
            if (year === 2023) return d.data2023;
            if (year === 2024) return d.data2024;
            if (year === 2025) return d.data2025;
          } else {
            // TTM series names
            if (series.name === 'Trailing 12 Months') return d.data2025;
            if (series.name === 'Previous TTM') return d.data2024;
          }
          return 0;
        }),
        deal_counts: aggregated.map(d => {
          // Use series.name to map to the correct deal counts, not index
          const year = parseInt(series.name, 10);
          if (!isNaN(year)) {
            // Year-based series (2023, 2024, 2025)
            if (year === 2023) return d.dealCounts2023 || 0;
            if (year === 2024) return d.dealCounts2024 || 0;
            if (year === 2025) return d.dealCounts2025 || 0;
          } else {
            // TTM series names
            if (series.name === 'Trailing 12 Months') return d.dealCounts2025 || 0;
            if (series.name === 'Previous TTM') return d.dealCounts2024 || 0;
          }
          return 0;
        })
      }))
    };

    return result;
  }, [maData, selectedGrouping, yearVisibility, timeframe, maCustomTitle, forceRefresh]);

  // Generate chart options to extract actual rendered titles
  const fundingChartOption = React.useMemo(() => {
    if (!aggregatedFundingData) return null;
    return getV2ChartOption({
      data: aggregatedFundingData,
      timeframe,
      weekOffset,
      timeGrouping: selectedGrouping,
      yearVisibility
    });
  }, [aggregatedFundingData, timeframe, weekOffset, selectedGrouping, yearVisibility, forceRefresh]);

  const maChartOption = React.useMemo(() => {
    if (!aggregatedMAData) return null;
    return getV2ChartOption({
      data: aggregatedMAData,
      timeframe,
      weekOffset,
      timeGrouping: selectedGrouping,
      yearVisibility
    });
  }, [aggregatedMAData, timeframe, weekOffset, selectedGrouping, yearVisibility, forceRefresh]);

  // Extract current titles from chart options (not data objects)
  // This ensures the editor shows what's actually rendered on the chart
  const fundingTitleOption = Array.isArray(fundingChartOption?.title) ? fundingChartOption.title[0] : fundingChartOption?.title;
  const fundingCurrentTitle = fundingTitleOption?.text || '';
  const fundingCurrentSubtitle = fundingTitleOption?.subtext || '';

  const maTitleOption = Array.isArray(maChartOption?.title) ? maChartOption.title[0] : maChartOption?.title;
  const maCurrentTitle = maTitleOption?.text || '';
  const maCurrentSubtitle = maTitleOption?.subtext || '';

  // Log performance metrics
  React.useEffect(() => {
    const startTime = Date.now();
    log.performance('UnifiedChartContainer rendered', Date.now(), 'Chart');

    // Track render performance
    performanceMonitor.trackRender(
      'UnifiedChartContainer',
      Date.now() - startTime,
      (fundingData?.x_axis?.length || 0) + (maData?.x_axis?.length || 0),
      {
        selectedGrouping,
        timeframe,
        fundingDataPoints: fundingData?.x_axis?.length || 0,
        maDataPoints: maData?.x_axis?.length || 0
      }
    );
  }, [fundingData, maData, selectedGrouping, timeframe]);

  return (
    <ChartErrorBoundary chartName="Unified Chart Container">
      <div className={`w-full ${containerSpacing}`}>
        {/* Time Grouping Controls - Removed duplicate, handled by V2App */}

        {/* Business Filters - REMOVED: Legacy non-functional filter UI replaced by FilterPanel in V2App */}

        {/* Year Visibility Controls - Removed duplicate, handled by V2App */}

        {/* Main Charts */}
        <div className="space-y-6">
          {/* Funding Chart */}
          {showFunding && (
            <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4 relative">
              <FundingChartWithHOC
                data={aggregatedFundingData}
                timeframe={timeframe}
                weekOffset={weekOffset}
                timeGrouping={selectedGrouping}
                yearVisibility={yearVisibility}
                chartType={timeframe === 'ttm' ? 'line' : 'bar'}
                height={500}
                title={aggregatedFundingData?.title}
                onLegendSelectChanged={handleLegendSelectChanged}
                onEditClick={() => setFundingEditorOpen(true)}
                hasCustomTitle={!!fundingCustomTitle}
              />
              {metadata && (
                <ChartMetadata
                  periodStart={metadata.current_period.start}
                  periodEnd={metadata.current_period.end}
                  cacheStatus={metadata.cache_status}
                  responseTimeMs={metadata.response_time_ms}
                />
              )}
            </div>
          )}

          {/* M&A Chart */}
          {showMA && (
            <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4 relative">
              <MAChartWithHOC
                data={aggregatedMAData}
                timeframe={timeframe}
                weekOffset={weekOffset}
                timeGrouping={selectedGrouping}
                yearVisibility={yearVisibility}
                chartType={timeframe === 'ttm' ? 'line' : 'bar'}
                height={500}
                title={aggregatedMAData?.title}
                onLegendSelectChanged={handleLegendSelectChanged}
                onEditClick={() => setMaEditorOpen(true)}
                hasCustomTitle={!!maCustomTitle}
              />
              {metadata && (
                <ChartMetadata
                  periodStart={metadata.current_period.start}
                  periodEnd={metadata.current_period.end}
                  cacheStatus={metadata.cache_status}
                  responseTimeMs={metadata.response_time_ms}
                />
              )}
            </div>
          )}
        </div>

        {/* Geographic Chart */}
        {geoData && weekLabel && (
          <React.Suspense
            fallback={
              <div className="flex items-center justify-center h-64 bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600"></div>
                <span className="ml-2 text-gray-600 dark:text-gray-400">Loading geographic chart...</span>
              </div>
            }
          >
            <CountryFundingChartV2
              data={geoData}
              weekLabel={weekLabel}
              timeframe={timeframe}
              weekOffset={weekOffset}
              filters={filters}
              metadata={metadata}
            />
          </React.Suspense>
        )}

        {/* Category Charts */}
        <React.Suspense fallback={<div className="animate-pulse bg-gray-200 h-64 rounded"></div>}>
          <ProductCategoryChart
            timeframe={timeframe}
            weekOffset={weekOffset}
            yearVisibility={yearVisibility}
            filters={filters}
            metadata={metadata}
          />
        </React.Suspense>

        <React.Suspense fallback={<div className="animate-pulse bg-gray-200 h-64 rounded"></div>}>
          <AcquisitionCategoryChart
            timeframe={timeframe}
            weekOffset={weekOffset}
            filters={filters}
            metadata={metadata}
          />
        </React.Suspense>

      </div>

      {/* Title Editor Modals */}
      <TitleEditor
        isOpen={fundingEditorOpen}
        onClose={() => setFundingEditorOpen(false)}
        currentTitle={fundingCurrentTitle}
        currentSubtitle={fundingCurrentSubtitle}
        onSave={(title, subtitle) => {
          customTitleService.setCustomTitle(fundingTitleKey, title, subtitle);
          setForceRefresh(prev => prev + 1);
          setFundingEditorOpen(false);
        }}
        onReset={() => {
          customTitleService.resetCustomTitle(fundingTitleKey);
          setForceRefresh(prev => prev + 1);
          setFundingEditorOpen(false);
        }}
        chartContext={{
          chartType: 'funding',
          timeframe,
          isMA: false
        }}
      />

      <TitleEditor
        isOpen={maEditorOpen}
        onClose={() => setMaEditorOpen(false)}
        currentTitle={maCurrentTitle}
        currentSubtitle={maCurrentSubtitle}
        onSave={(title, subtitle) => {
          customTitleService.setCustomTitle(maTitleKey, title, subtitle);
          setForceRefresh(prev => prev + 1);
          setMaEditorOpen(false);
        }}
        onReset={() => {
          customTitleService.resetCustomTitle(maTitleKey);
          setForceRefresh(prev => prev + 1);
          setMaEditorOpen(false);
        }}
        chartContext={{
          chartType: 'M&A',
          timeframe,
          isMA: true
        }}
      />
    </ChartErrorBoundary>
  );
};

export default UnifiedChartContainer;
